import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  TbArrowLeft, TbBuilding, TbMapPin,
  TbPhoto, TbCheck, TbAlertCircle, TbUser,
  TbX, TbChevronDown, TbChevronUp, TbLoader
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";
const CLOUDINARY_URL = "https://api.cloudinary.com/v1_1/dhwqnwnvh/image/upload";
const CLOUDINARY_PRESET = "komorotel_unsigned";

const ISLANDS = ["Ngazidja", "Anjouan", "Moheli"];
const TYPES = [
  { value: "hotel",              label: "Hôtel" },
  { value: "chambre_hote",       label: "Chambre d'hôte" },
  { value: "appartement_studio", label: "Appartement / Studio" },
];

const EQUIPMENTS = {
  "Confort":       ["Climatisation", "Chauffage", "Ventilateur", "Canapé-lit", "Bureau", "Coffre-fort"],
  "Cuisine":       ["Cuisine équipée", "Réfrigérateur", "Micro-ondes", "Cafetière", "Bouilloire", "Lave-vaisselle"],
  "Salle de bain": ["Sèche-cheveux", "Serviettes", "Produits de toilette", "Baignoire", "Douche à l'italienne"],
  "Connectivité":  ["Wifi", "TV écran plat", "Netflix", "Prises USB"],
  "Extérieur":     ["Terrasse", "Balcon", "Vue mer", "Jardin", "Parking", "Piscine"],
  "Services":      ["Linge de lit fourni", "Ménage inclus", "Accueil personnalisé", "Conciergerie"],
};

// ─────────────────────────────────────────
// FIELD GROUP
// ─────────────────────────────────────────
function FieldGroup({ label, children, error }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-gray-500">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Input({ error, ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2.5 text-sm border rounded-xl outline-none transition
        focus:ring-2 focus:ring-[#3A9D23]/10
        ${error ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-[#3A9D23]"}`}
    />
  );
}

function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none bg-white transition focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10"
    >
      {children}
    </select>
  );
}

// ─────────────────────────────────────────
// PHOTOS UPLOADER
// ─────────────────────────────────────────
function PhotosSection({ photos, setPhotos }) {
  const [uploading, setUploading] = useState(false);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_PRESET);
    const res = await fetch(CLOUDINARY_URL, { method: "POST", body: formData });
    const data = await res.json();
    return data.secure_url;
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    try {
      const urls = await Promise.all(acceptedFiles.map(uploadToCloudinary));
      setPhotos(p => [...p, ...urls]);
    } catch {
      console.error("Erreur upload");
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
  });

  const removePhoto = (i) => setPhotos(p => p.filter((_, idx) => idx !== i));

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
          <TbPhoto className="text-blue-600" size={14} />
        </div>
        <p className="text-sm font-semibold text-gray-900">Photos</p>
        {photos.length > 0 && (
          <span className="ml-auto text-xs text-gray-400">{photos.length} photo{photos.length > 1 ? "s" : ""}</span>
        )}
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition
          ${isDragActive
            ? "border-[#3A9D23] bg-[#3A9D23]/5"
            : "border-gray-200 hover:border-[#3A9D23]/40 hover:bg-gray-50"
          }`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <TbLoader className="text-[#3A9D23] animate-spin" size={22} />
            <p className="text-xs text-gray-400">Upload en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <TbPhoto className="text-gray-300" size={28} />
            <p className="text-sm text-gray-500">
              {isDragActive ? "Déposez ici" : "Glissez vos images ou cliquez"}
            </p>
            <p className="text-xs text-gray-400">JPG, PNG, WEBP — plusieurs fichiers acceptés</p>
          </div>
        )}
      </div>

      {/* Grille photos */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((url, i) => (
            <div key={i} className="relative rounded-xl overflow-hidden h-24 bg-gray-100 group">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
              <button
                onClick={() => removePhoto(i)}
                className="absolute top-1.5 right-1.5 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow-sm hover:bg-red-50"
              >
                <TbX size={12} className="text-gray-600 hover:text-red-500" />
              </button>
              {i === 0 && (
                <span className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 bg-black/50 text-white rounded-md">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// ÉQUIPEMENTS — accordéon par catégorie
// ─────────────────────────────────────────
function EquipmentsSection({ selected, setSelected }) {
  const [openCats, setOpenCats] = useState(["Confort"]);

  const toggleCat = (cat) => {
    setOpenCats(o => o.includes(cat) ? o.filter(c => c !== cat) : [...o, cat]);
  };

  const toggleItem = (cat, name) => {
    setSelected(s => {
      const exists = s.find(e => e.name === name && e.category === cat);
      if (exists) return s.filter(e => !(e.name === name && e.category === cat));
      return [...s, { name, category: cat }];
    });
  };

  const isSelected = (cat, name) => selected.some(e => e.name === name && e.category === cat);
  const countCat   = (cat) => selected.filter(e => e.category === cat).length;
  const totalSelected = selected.length;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center">
            <TbCheck className="text-violet-600" size={14} />
          </div>
          <p className="text-sm font-semibold text-gray-900">Équipements</p>
        </div>
        {totalSelected > 0 && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#eaf3de] text-[#3B6D11]">
            {totalSelected} sélectionné{totalSelected > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Chips sélectionnés */}
      {totalSelected > 0 && (
        <div className="flex flex-wrap gap-1.5 pb-1">
          {selected.map((eq, i) => (
            <span
              key={i}
              className="flex items-center gap-1 px-2.5 py-1 bg-[#eaf3de] text-[#3B6D11] rounded-lg text-xs font-medium"
            >
              {eq.name}
              <button
                onClick={() => toggleItem(eq.category, eq.name)}
                className="hover:text-red-500 transition ml-0.5"
              >
                <TbX size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Accordéon */}
      <div className="flex flex-col gap-1.5">
        {Object.entries(EQUIPMENTS).map(([cat, items]) => {
          const isOpen  = openCats.includes(cat);
          const count   = countCat(cat);
          return (
            <div key={cat} className="border border-gray-100 rounded-xl overflow-hidden">
              {/* Header catégorie */}
              <button
                onClick={() => toggleCat(cat)}
                className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{cat}</span>
                  {count > 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#eaf3de] text-[#3B6D11]">
                      {count}
                    </span>
                  )}
                </div>
                {isOpen
                  ? <TbChevronUp size={15} className="text-gray-400" />
                  : <TbChevronDown size={15} className="text-gray-400" />
                }
              </button>

              {/* Items */}
              {isOpen && (
                <div className="px-3.5 pb-3 pt-1 flex flex-wrap gap-2 border-t border-gray-50">
                  {items.map(name => {
                    const active = isSelected(cat, name);
                    return (
                      <button
                        key={name}
                        onClick={() => toggleItem(cat, name)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border
                          ${active
                            ? "bg-[#eaf3de] text-[#3B6D11] border-[#3A9D23]/20"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[#3A9D23]/30 hover:bg-gray-50"
                          }`}
                      >
                        {active && <TbCheck size={12} />}
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// PAGE PRINCIPALE
// ─────────────────────────────────────────
export default function AgencyCreateHotel({ setActivePage }) {
  const [owners, setOwners]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors]   = useState({});
  const [photos, setPhotos]   = useState([]);
  const [equipments, setEquipments] = useState([]);

  const [form, setForm] = useState({
    name:    "",
    type:    "appartement_studio",
    island:  "Ngazidja",
    city:    "",
    adress:  "",
    email:   "",
    nif:     "",
    desc:    "",
    ownerId: "",
  });

  useEffect(() => {
    axios.get(`${API}/owners`, { withCredentials: true })
      .then(r => setOwners(r.data))
      .catch(console.error);
  }, []);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(er => ({ ...er, [e.target.name]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())   e.name   = "Nom requis.";
    if (!form.city.trim())   e.city   = "Ville requise.";
    if (!form.adress.trim()) e.adress = "Adresse requise.";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setLoading(true);
    try {
      await axios.post(`${API}`, {
        ...form,
        ownerId:    form.ownerId || null,
        photos,
        equipments,
      }, { withCredentials: true });

      setSuccess(true);
      setTimeout(() => setActivePage("hotels"), 1500);
    } catch (err) {
      setErrors({ global: err.response?.data?.message || "Erreur lors de la création." });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center">
            <TbCheck className="text-emerald-600" size={28} />
          </div>
          <p className="text-sm font-medium text-gray-700">Bien créé avec succès !</p>
          <p className="text-xs text-gray-400">Redirection vers vos biens...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setActivePage("hotels")}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-400 hover:bg-gray-50 transition"
        >
          <TbArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Ajouter un bien</h1>
          <p className="text-sm text-gray-400 mt-0.5">Renseigner les informations du logement</p>
        </div>
      </div>

      {/* ── ERREUR GLOBALE ── */}
      {errors.global && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
          <TbAlertCircle className="text-red-500 shrink-0" size={18} />
          <p className="text-sm text-red-600">{errors.global}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── COL GAUCHE ── */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Infos de base */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                <TbBuilding className="text-[#3A9D23]" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Informations générales</p>
            </div>

            <FieldGroup label="Nom du bien *" error={errors.name}>
              <Input name="name" value={form.name} onChange={handleChange}
                placeholder="Ex : Appartement Vue Mer Moroni" error={errors.name} />
            </FieldGroup>

            <div className="grid grid-cols-2 gap-4">
              <FieldGroup label="Type de bien *">
                <Select name="type" value={form.type} onChange={handleChange}>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </FieldGroup>
              <FieldGroup label="Île *">
                <Select name="island" value={form.island} onChange={handleChange}>
                  {ISLANDS.map(i => <option key={i} value={i}>{i}</option>)}
                </Select>
              </FieldGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FieldGroup label="Ville *" error={errors.city}>
                <Input name="city" value={form.city} onChange={handleChange}
                  placeholder="Ex : Moroni" error={errors.city} />
              </FieldGroup>
              <FieldGroup label="Email du bien">
                <Input type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="contact@bien.com" />
              </FieldGroup>
            </div>

            <FieldGroup label="Adresse *" error={errors.adress}>
              <Input name="adress" value={form.adress} onChange={handleChange}
                placeholder="Ex : Rue de la Corniche" error={errors.adress} />
            </FieldGroup>

            <FieldGroup label="NIF (optionnel)">
              <Input name="nif" value={form.nif} onChange={handleChange}
                placeholder="Numéro d'identification fiscale" />
            </FieldGroup>

            <FieldGroup label="Description">
              <textarea
                name="desc" value={form.desc} onChange={handleChange} rows={4}
                placeholder="Décrivez le bien : vue, ambiance, points forts..."
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition resize-none"
              />
            </FieldGroup>
          </div>

          {/* Photos */}
          <PhotosSection photos={photos} setPhotos={setPhotos} />

          {/* Équipements */}
          <EquipmentsSection selected={equipments} setSelected={setEquipments} />
        </div>

        {/* ── COL DROITE ── */}
        <div className="flex flex-col gap-4">

          {/* Propriétaire */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                <TbUser className="text-amber-600" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Propriétaire</p>
            </div>
            <FieldGroup label="Associer à un propriétaire">
              <Select name="ownerId" value={form.ownerId} onChange={handleChange}>
                <option value="">— Aucun pour l'instant —</option>
                {owners.map(o => (
                  <option key={o._id} value={o._id}>{o.name}</option>
                ))}
              </Select>
            </FieldGroup>
            <p className="text-xs text-gray-400">
              Vous pouvez associer un propriétaire maintenant ou plus tard.
            </p>
            {owners.length === 0 && (
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-xs text-amber-700">
                  Aucun propriétaire enregistré. Ajoutez-en un depuis la section Propriétaires.
                </p>
              </div>
            )}
          </div>

          {/* Récapitulatif */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                <TbMapPin className="text-[#3A9D23]" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Récapitulatif</p>
            </div>
            {[
              { label: "Nom",         value: form.name   || "—" },
              { label: "Type",        value: TYPES.find(t => t.value === form.type)?.label || "—" },
              { label: "Île",         value: form.island },
              { label: "Ville",       value: form.city   || "—" },
              { label: "Photos",      value: `${photos.length} photo${photos.length > 1 ? "s" : ""}` },
              { label: "Équipements", value: `${equipments.length} sélectionné${equipments.length > 1 ? "s" : ""}` },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                <p className="text-xs text-gray-400">{item.label}</p>
                <p className="text-xs font-medium text-gray-700 truncate max-w-[140px]">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 bg-[#3A9D23] text-white text-sm font-medium rounded-2xl hover:bg-[#2d8019] transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading
              ? <><TbLoader size={16} className="animate-spin" /> Création en cours...</>
              : <><TbCheck size={16} /> Créer le bien</>
            }
          </button>

          <button
            onClick={() => setActivePage("hotels")}
            className="w-full py-2.5 border border-gray-200 text-gray-500 text-sm rounded-2xl hover:bg-gray-50 transition"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}